import { StandardEvents } from '@shopify/events';
import { DrawerCloseEvent, DrawerOpenEvent } from '@theme/theme-drawer';

const INITIAL_FRAME_DELAY = 2;
const MINIMUM_UPDATING_DURATION = 640;
const PROGRESS_TRANSITION_DURATION = 520;
const SUCCESS_ICON_DELAY = 360;
const SUCCESS_HALO_DURATION = 560;
const MESSAGE_MOTION_DURATION = 1160;
const UPDATE_SAFETY_TIMEOUT = 10000;

class RiftCartFreeShipping extends HTMLElement {
  static #surfaceState = new Map();

  static observedAttributes = [
    'data-rift-progress',
    'data-rift-display-percent',
    'data-rift-achieved',
    'data-rift-state',
  ];

  #abortController;
  #runtime;
  #motion;
  #normalMessage;
  #updatingMessage;
  #progressbar;
  #drawer;
  #confirmedState;
  #pendingServerState;
  #connected = false;
  #refreshQueued = false;
  #isUpdating = false;
  #promiseSettled = false;
  #serverStateReady = false;
  #updateStartedAt = 0;
  #updateSequence = 0;
  #messageTimer;
  #messageMotionTimer;
  #minimumTimer;
  #safetyTimer;
  #successIconTimer;
  #celebrationStartTimer;
  #celebrationEndTimer;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#connected = true;
    this.#cacheElements();
    this.#clearTimers();

    if (!this.#runtime || !this.#motion) return;

    const surface = this.dataset.riftSurface || 'cart';
    const targetState = this.#readServerState();
    const previousState = RiftCartFreeShipping.#surfaceState.get(surface);
    const isDrawer = surface === 'drawer';
    const shouldAnimateInitial = !isDrawer && this.#animationEnabled() && !previousState?.hasAnimatedInitial;
    const initialProgress = shouldAnimateInitial
      ? 0
      : this.#clampProgress(previousState?.visualProgress ?? targetState.progress);

    this.#runtime.classList.remove(
      'is-updating',
      'is-message-changing',
      'is-message-bouncing',
      'is-celebrating',
      'is-surface-active'
    );
    this.#runtime.classList.add('is-enhanced', 'is-initializing');
    this.#runtime.classList.toggle('is-visual-achieved', targetState.achieved);
    this.#motion.style.setProperty('--rift-free-shipping-visual-progress', String(initialProgress));
    this.#syncUpdatingUi(false, targetState);
    this.#confirmedState = targetState;

    void this.#motion.offsetWidth;
    this.#runtime.classList.remove('is-initializing');

    if (shouldAnimateInitial && initialProgress !== targetState.progress) {
      this.#afterAnimationFrames(INITIAL_FRAME_DELAY, () => this.#setVisualProgress(targetState.progress));
    } else {
      this.#setVisualProgress(targetState.progress);
    }

    if (!isDrawer) {
      this.#runtime.classList.add('is-surface-active');
      this.#triggerMessageMotion();
    }

    this.#storeSurfaceState({
      progress: targetState.progress,
      achieved: targetState.achieved,
      message: targetState.message,
      visualProgress: targetState.progress,
      hasAnimatedInitial: true,
    });

    const { signal } = this.#abortController;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartMutation, { signal });
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleCartMutation, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartError, { signal });

    this.#drawer = isDrawer ? this.closest('theme-drawer') : null;
    this.#drawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen, { signal });
    this.#drawer?.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose, { signal });

    if (this.#drawer?.hasAttribute('open')) {
      this.#afterAnimationFrames(1, this.#handleDrawerOpen);
    }
  }

  disconnectedCallback() {
    this.#connected = false;
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#clearTimers();

    const visualProgress = this.#getVisualProgress();
    const state = this.#confirmedState || this.#readServerState();

    this.#storeSurfaceState({
      progress: state.progress,
      achieved: state.achieved,
      message: state.message,
      visualProgress,
      hasAnimatedInitial: true,
    });
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (!this.#connected || oldValue === newValue) return;
    this.#queueServerRefresh();
  }

  #handleDrawerOpen = () => {
    if (!this.#runtime || !this.#motion || !this.#connected) return;

    const targetState = this.#readServerState();
    this.#runtime.classList.add('is-surface-active');
    this.#runtime.classList.remove('is-message-bouncing', 'is-celebrating');

    if (!this.#animationEnabled()) {
      this.#runtime.classList.toggle('is-visual-achieved', targetState.achieved);
      this.#setVisualProgress(targetState.progress);
      return;
    }

    this.#clearSuccessTimers();
    this.#runtime.classList.add('is-initializing');
    this.#runtime.classList.remove('is-visual-achieved');
    this.#setVisualProgress(0);
    void this.#motion.offsetWidth;
    this.#runtime.classList.remove('is-initializing');

    this.#afterAnimationFrames(INITIAL_FRAME_DELAY, () => {
      this.#setVisualProgress(targetState.progress);
      if (targetState.achieved) this.#scheduleSuccessState();
    });

    this.#triggerMessageMotion();
  };

  #handleDrawerClose = () => {
    this.#runtime?.classList.remove('is-surface-active', 'is-message-bouncing', 'is-celebrating');
    window.clearTimeout(this.#messageMotionTimer);
  };

  #handleCartMutation = (event) => {
    const sequence = ++this.#updateSequence;
    const updatePromise = event.promise;

    this.#beginUpdating();

    if (!updatePromise || typeof updatePromise.then !== 'function') {
      this.#promiseSettled = true;
      this.#afterAnimationFrames(INITIAL_FRAME_DELAY, () => {
        if (sequence !== this.#updateSequence) return;
        this.#pendingServerState = this.#readServerState();
        this.#serverStateReady = true;
        this.#tryCompleteUpdate(sequence);
      });
      return;
    }

    Promise.resolve(updatePromise)
      .then(() => {
        if (sequence !== this.#updateSequence) return;
        this.#promiseSettled = true;

        this.#afterAnimationFrames(INITIAL_FRAME_DELAY, () => {
          if (sequence !== this.#updateSequence) return;
          this.#pendingServerState ||= this.#readServerState();
          this.#serverStateReady = true;
          this.#tryCompleteUpdate(sequence);
        });
      })
      .catch(() => {
        if (sequence !== this.#updateSequence) return;
        this.#finishFailedUpdate(sequence);
      });
  };

  #handleCartError = () => {
    if (!this.#isUpdating) return;
    this.#finishFailedUpdate(this.#updateSequence);
  };

  #beginUpdating() {
    this.#clearUpdateTimers();
    this.#clearSuccessTimers();
    this.#cacheElements();
    this.#runtime?.classList.remove('is-celebrating', 'is-message-bouncing');
    this.#isUpdating = true;
    this.#promiseSettled = false;
    this.#serverStateReady = false;
    this.#pendingServerState = undefined;
    this.#updateStartedAt = performance.now();
    this.#syncUpdatingUi(true, this.#confirmedState);

    this.#safetyTimer = window.setTimeout(() => {
      this.#finishFailedUpdate(this.#updateSequence);
    }, UPDATE_SAFETY_TIMEOUT);
  }

  #queueServerRefresh() {
    if (this.#refreshQueued) return;
    this.#refreshQueued = true;

    queueMicrotask(() => {
      this.#refreshQueued = false;
      if (!this.isConnected) return;

      this.#cacheElements();
      const targetState = this.#readServerState();

      if (this.#isUpdating) {
        this.#pendingServerState = targetState;
        this.#serverStateReady = true;
        this.#syncUpdatingUi(true, this.#confirmedState);
        this.#tryCompleteUpdate(this.#updateSequence);
        return;
      }

      this.#applyServerState(targetState, { animateCopy: true });
    });
  }

  #tryCompleteUpdate(sequence) {
    if (!this.#isUpdating || sequence !== this.#updateSequence) return;
    if (!this.#promiseSettled || !this.#serverStateReady || !this.#pendingServerState) return;

    const minimumDuration = this.#animationEnabled() ? MINIMUM_UPDATING_DURATION : 0;
    const remaining = Math.max(0, minimumDuration - (performance.now() - this.#updateStartedAt));

    window.clearTimeout(this.#minimumTimer);
    this.#minimumTimer = window.setTimeout(() => {
      if (sequence !== this.#updateSequence || !this.#pendingServerState) return;

      const targetState = this.#pendingServerState;
      this.#isUpdating = false;
      this.#syncUpdatingUi(false, targetState);
      this.#applyServerState(targetState, { animateCopy: true });
      this.#resetPendingUpdate();
    }, remaining);
  }

  #finishFailedUpdate(sequence) {
    if (sequence !== this.#updateSequence) return;

    const minimumDuration = this.#animationEnabled() ? MINIMUM_UPDATING_DURATION : 0;
    const remaining = Math.max(0, minimumDuration - (performance.now() - this.#updateStartedAt));

    window.clearTimeout(this.#minimumTimer);
    this.#minimumTimer = window.setTimeout(() => {
      if (sequence !== this.#updateSequence) return;
      this.#isUpdating = false;
      this.#syncUpdatingUi(false, this.#confirmedState);
      this.#animateCopy();
      this.#resetPendingUpdate();
    }, remaining);
  }

  #applyServerState(targetState, { animateCopy = false } = {}) {
    if (!targetState || !this.#runtime || !this.#motion) return;

    const previousState = this.#confirmedState || targetState;
    const crossedThreshold = !previousState.achieved && targetState.achieved;

    this.#clearSuccessTimers();

    if (!targetState.achieved) {
      this.#runtime.classList.remove('is-visual-achieved', 'is-celebrating');
    }

    this.#setVisualProgress(targetState.progress);

    if (targetState.achieved) {
      if (crossedThreshold && this.#animationEnabled()) this.#scheduleSuccessState();
      else this.#runtime.classList.add('is-visual-achieved');
    }

    if (animateCopy && (previousState.message !== targetState.message || previousState.displayPercent !== targetState.displayPercent)) {
      this.#animateCopy();
    }

    this.#confirmedState = targetState;
    this.#syncProgressbar(targetState);
    this.#storeSurfaceState({
      progress: targetState.progress,
      achieved: targetState.achieved,
      message: targetState.message,
      visualProgress: targetState.progress,
      hasAnimatedInitial: true,
    });
  }

  #scheduleSuccessState() {
    this.#successIconTimer = window.setTimeout(() => {
      this.#runtime?.classList.add('is-visual-achieved');
    }, SUCCESS_ICON_DELAY);

    if (this.dataset.riftAnimation !== 'celebration') return;

    this.#celebrationStartTimer = window.setTimeout(() => {
      this.#runtime?.classList.add('is-celebrating');
    }, PROGRESS_TRANSITION_DURATION);
    this.#celebrationEndTimer = window.setTimeout(() => {
      this.#runtime?.classList.remove('is-celebrating');
    }, PROGRESS_TRANSITION_DURATION + SUCCESS_HALO_DURATION + 40);
  }

  #syncUpdatingUi(updating, state) {
    if (!this.#runtime) return;

    this.#runtime.classList.toggle('is-updating', updating);
    this.setAttribute('aria-busy', String(updating));
    this.#toggleHidden(this.#normalMessage, updating);
    this.#toggleHidden(this.#updatingMessage, !updating);

    if (!this.#progressbar) return;

    if (updating) {
      this.#progressbar.setAttribute('aria-valuenow', String(state?.displayPercent ?? 0));
      this.#progressbar.setAttribute('aria-valuetext', this.#updatingMessage?.textContent?.trim() || '');
    } else {
      this.#syncProgressbar(state || this.#readServerState());
    }
  }

  #syncProgressbar(state) {
    if (!this.#progressbar || !state) return;
    this.#progressbar.setAttribute('aria-valuenow', String(state.displayPercent));
    this.#progressbar.setAttribute('aria-valuetext', state.ariaValueText || state.message);
  }

  #animateCopy() {
    if (!this.#runtime || !this.#animationEnabled()) return;

    window.clearTimeout(this.#messageTimer);
    this.#runtime.classList.remove('is-message-changing');

    requestAnimationFrame(() => {
      if (!this.isConnected || !this.#runtime) return;
      this.#runtime.classList.add('is-message-changing');
      this.#messageTimer = window.setTimeout(() => {
        this.#runtime?.classList.remove('is-message-changing');
      }, 220);
    });
  }

  #triggerMessageMotion() {
    if (!this.#runtime || !this.#animationEnabled() || this.dataset.riftAchieved === 'true') return;
    if (this.dataset.riftMessageMotion !== 'on_open') return;

    window.clearTimeout(this.#messageMotionTimer);
    this.#runtime.classList.remove('is-message-bouncing');
    requestAnimationFrame(() => {
      if (!this.isConnected || !this.#runtime) return;
      this.#runtime.classList.add('is-message-bouncing');
      this.#messageMotionTimer = window.setTimeout(() => {
        this.#runtime?.classList.remove('is-message-bouncing');
      }, MESSAGE_MOTION_DURATION);
    });
  }

  #cacheElements() {
    this.#runtime = this.querySelector('[data-rift-runtime]');
    this.#motion = this.querySelector('[data-rift-progress-motion]');
    this.#normalMessage = this.querySelector('[data-rift-message-normal]');
    this.#updatingMessage = this.querySelector('[data-rift-message-updating]');
    this.#progressbar = this.querySelector('[data-rift-progressbar]');
  }

  #readServerState() {
    const progress = this.#clampProgress(Number.parseFloat(this.dataset.riftProgress || '0'));
    const displayPercent = Math.min(100, Math.max(0, Number.parseInt(this.dataset.riftDisplayPercent || '0', 10) || 0));
    const message = this.#normalMessage?.textContent?.trim() || '';

    return {
      progress,
      displayPercent,
      achieved: this.dataset.riftAchieved === 'true',
      state: this.dataset.riftState || 'progress',
      message,
      ariaValueText: this.#progressbar?.getAttribute('aria-valuetext') || message,
    };
  }

  #setVisualProgress(progress) {
    this.#motion?.style.setProperty('--rift-free-shipping-visual-progress', String(this.#clampProgress(progress)));
  }

  #getVisualProgress() {
    if (!this.#motion) return this.#confirmedState?.progress ?? 0;
    const computedProgress = Number.parseFloat(
      getComputedStyle(this.#motion).getPropertyValue('--rift-free-shipping-visual-progress')
    );
    return Number.isFinite(computedProgress) ? this.#clampProgress(computedProgress) : this.#confirmedState?.progress ?? 0;
  }

  #storeSurfaceState(state) {
    RiftCartFreeShipping.#surfaceState.set(this.dataset.riftSurface || 'cart', state);
  }

  #toggleHidden(element, hidden) {
    if (!(element instanceof HTMLElement)) return;
    element.hidden = hidden;
  }

  #afterAnimationFrames(frameCount, callback) {
    const next = (remaining) => {
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        if (remaining <= 1) callback();
        else next(remaining - 1);
      });
    };
    next(frameCount);
  }

  #resetPendingUpdate() {
    window.clearTimeout(this.#minimumTimer);
    window.clearTimeout(this.#safetyTimer);
    this.#promiseSettled = false;
    this.#serverStateReady = false;
    this.#pendingServerState = undefined;
  }

  #clearUpdateTimers() {
    window.clearTimeout(this.#minimumTimer);
    window.clearTimeout(this.#safetyTimer);
  }

  #clearSuccessTimers() {
    window.clearTimeout(this.#successIconTimer);
    window.clearTimeout(this.#celebrationStartTimer);
    window.clearTimeout(this.#celebrationEndTimer);
  }

  #clearTimers() {
    window.clearTimeout(this.#messageTimer);
    window.clearTimeout(this.#messageMotionTimer);
    this.#clearUpdateTimers();
    this.#clearSuccessTimers();
  }

  #animationEnabled() {
    return this.dataset.riftAnimation !== 'none' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #clampProgress(progress) {
    if (!Number.isFinite(progress)) return 0;
    return Math.min(100, Math.max(0, progress));
  }
}
if (!customElements.get('rift-cart-free-shipping')) {
  customElements.define('rift-cart-free-shipping', RiftCartFreeShipping);
}
